import { VehicleType } from "@/src/types/types";

/**
 * Route constants for vertical import functionality
 * Centralized route paths used in vertical ad transfer flow
 */
export const ROUTES = {
  CONSUMER: {
    HOME: "/",
    HOME_MOTO: "/moto",
    /**
     * Beely partner entry: `/beely` or `/beely?regno=…&mileage=…`.
     * Always redirects to car `/auction/create` with `source=beely` and other query preserved.
     */
    BEELY_INTEGRATION: "/beely",
    HOME_CARAVAN: "/caravan",
    AUCTION_EDIT: "/auction/edit",
    AUCTION_EDIT_MOTO: "/moto/auction/edit",
    AUCTION_EDIT_CARAVAN: "/caravan/auction/edit",
    AUCTION_CREATE: "/auction/create",
    AUCTION_CREATE_MOTO: "/moto/auction/create",
    AUCTION_CREATE_CARAVAN: "/caravan/auction/create",
    MY_AUCTIONS: "/my-auctions",
    MY_AUCTION_DETAILS: "/my-auctions/:auctionId"
  }
} as const;

const EDIT_ROUTE_MAP: Record<VehicleType, string> = {
  [VehicleType.CAR]: ROUTES.CONSUMER.AUCTION_EDIT,
  [VehicleType.MOTO]: ROUTES.CONSUMER.AUCTION_EDIT_MOTO,
  [VehicleType.CARAVAN]: ROUTES.CONSUMER.AUCTION_EDIT_CARAVAN
};

const CREATE_ROUTE_MAP: Record<VehicleType, string> = {
  [VehicleType.CAR]: ROUTES.CONSUMER.AUCTION_CREATE,
  [VehicleType.MOTO]: ROUTES.CONSUMER.AUCTION_CREATE_MOTO,
  [VehicleType.CARAVAN]: ROUTES.CONSUMER.AUCTION_CREATE_CARAVAN
};

const HOME_ROUTE_MAP: Record<VehicleType, string> = {
  [VehicleType.CAR]: ROUTES.CONSUMER.HOME,
  [VehicleType.MOTO]: ROUTES.CONSUMER.HOME_MOTO,
  [VehicleType.CARAVAN]: ROUTES.CONSUMER.HOME_CARAVAN
};

export const getAuctionEditRoute = (vehicleType: VehicleType): string => {
  return EDIT_ROUTE_MAP[vehicleType];
};

export const getAuctionCreateRoute = (vehicleType: VehicleType): string => {
  return CREATE_ROUTE_MAP[vehicleType];
};

export const getHomeRoute = (vehicleType: VehicleType): string => {
  return HOME_ROUTE_MAP[vehicleType];
};

export const isMotoRoute = (pathname: string): boolean => {
  return pathname.includes("/moto");
};

export const isCaravanRoute = (pathname: string): boolean => {
  return pathname.includes("/caravan");
};

/**
 * Maps a consumer auction CTA href (relative or absolute) to the vertical
 * used for wizard routes. Defaults to car when missing or unrecognized.
 */
export const vehicleTypeFromAuctionHref = (
  href: string | undefined | null
): VehicleType => {
  const raw = href?.trim() ?? "";
  if (raw === "") {
    return VehicleType.CAR;
  }
  let path = raw;
  if (raw.includes("://")) {
    try {
      path = new URL(raw).pathname;
    } catch {
      path = raw;
    }
  }
  if (path.includes("/caravan")) {
    return VehicleType.CARAVAN;
  }
  if (path.includes("/moto")) {
    return VehicleType.MOTO;
  }
  return VehicleType.CAR;
};

export const getAuctionEditRouteFromPathname = (pathname: string): string => {
  if (isCaravanRoute(pathname)) return ROUTES.CONSUMER.AUCTION_EDIT_CARAVAN;
  if (isMotoRoute(pathname)) return ROUTES.CONSUMER.AUCTION_EDIT_MOTO;
  return ROUTES.CONSUMER.AUCTION_EDIT;
};

export const isEditRoute = (pathname: string): boolean => {
  return (
    pathname.includes(ROUTES.CONSUMER.AUCTION_EDIT) ||
    pathname.includes(ROUTES.CONSUMER.AUCTION_EDIT_MOTO) ||
    pathname.includes(ROUTES.CONSUMER.AUCTION_EDIT_CARAVAN)
  );
};

export const isMyAuctionsRoute = (pathname: string): boolean => {
  return pathname.includes(ROUTES.CONSUMER.MY_AUCTIONS);
};

export const isOnWizardRoute = (pathname: string): boolean => {
  return (
    pathname.includes("/auction/create") ||
    pathname.includes("/auction/edit") ||
    pathname.includes("/moto/auction/create") ||
    pathname.includes("/moto/auction/edit") ||
    pathname.includes("/caravan/auction/create") ||
    pathname.includes("/caravan/auction/edit")
  );
};
